import { supabase } from './supabase'
import { getInvoiceStorageMode } from './medical-invoices'
import type { MedicalInvoice, Reimbursement } from '@/types/health'

interface ReimbursementStatus {
  id: string
  invoice_id: string
  status: 'pending' | 'processing' | 'approved' | 'rejected' | 'paid'
  amount_reimbursed: number
  processing_date?: string
  payment_date?: string
  rejection_reason?: string
  notes?: string
}

type ReimbursementRow = Reimbursement & {
  processing_notes?: string | null
}

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function toStringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseFloat(value.replace(',', '.'))
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return fallback
}

function isMissingRelationError(error: unknown, relationNames: string[]): boolean {
  if (!error || typeof error !== 'object') return false
  const code = String((error as { code?: unknown }).code || '')
  const message = String((error as { message?: unknown }).message || '')
  if (code !== 'PGRST205' && code !== '42P01') return false
  return relationNames.some((relationName) => message.includes(relationName))
}

function getSafeMetadata(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

function mapStoredStatusToUi(status: string): ReimbursementStatus['status'] {
  switch (status) {
    case 'processed':
      return 'paid'
    case 'partial':
      return 'processing'
    case 'rejected':
      return 'rejected'
    case 'pending':
    default:
      return 'pending'
  }
}

function mapUiStatusToStored(status: ReimbursementStatus['status']) {
  switch (status) {
    case 'paid':
      return 'processed'
    case 'processing':
    case 'approved':
      return 'partial'
    case 'rejected':
      return 'rejected'
    case 'pending':
    default:
      return 'pending'
  }
}

function enrichInvoice(invoice: MedicalInvoice) {
  const metadata = getSafeMetadata(invoice.metadata)
  const providerName = invoice.practitioner_name || invoice.medical_facility || 'Facture médicale'

  return {
    ...invoice,
    provider_name: providerName,
    insurance_provider: toStringValue(metadata.insurance_provider),
    consultation_type: toStringValue(metadata.consultation_type)
  }
}

async function isReimbursementFeatureAvailable(): Promise<boolean> {
  const mode = await getInvoiceStorageMode()
  if (mode !== 'table') {
    return false
  }

  const { error } = await supabase.from('reimbursements').select('id', { count: 'exact', head: true })

  if (!error) {
    return true
  }

  if (isMissingRelationError(error, ['public.reimbursements', 'reimbursements'])) {
    return false
  }

  throw new Error(`Erreur vérification remboursements: ${error.message}`)
}

async function listTrackedReimbursements(
  patientId: string,
  statuses: Array<'pending' | 'partial' | 'processed' | 'rejected'>
): Promise<Array<MedicalInvoice & { reimbursement: ReimbursementRow }>> {
  if (!await isReimbursementFeatureAvailable()) {
    return []
  }

  const { data: reimbursements, error } = await supabase
    .from('reimbursements')
    .select('*')
    .eq('patient_id', patientId)
    .in('status', statuses)
    .order('created_at', { ascending: false })

  if (error) {
    if (isMissingRelationError(error, ['public.reimbursements', 'reimbursements'])) {
      return []
    }
    throw new Error(`Erreur récupération remboursements: ${error.message}`)
  }

  const reimbursementRows = (reimbursements || []) as Array<Record<string, unknown>>
  const invoiceIds = [...new Set(
    reimbursementRows
      .map((row) => toStringValue(row.invoice_id))
      .filter((value): value is string => Boolean(value))
  )]

  if (invoiceIds.length === 0) {
    return []
  }

  const { data: invoices, error: invoiceError } = await supabase
    .from('medical_invoices')
    .select('*')
    .in('id', invoiceIds)

  if (invoiceError) {
    if (isMissingRelationError(invoiceError, ['public.medical_invoices', 'medical_invoices'])) {
      return []
    }
    throw new Error(`Erreur récupération factures remboursements: ${invoiceError.message}`)
  }

  const invoiceMap = new Map<string, MedicalInvoice>()
  ;((invoices || []) as MedicalInvoice[]).forEach((invoice) => {
    invoiceMap.set(invoice.id, invoice)
  })

  return reimbursementRows
    .map((row) => {
      const invoiceId = toStringValue(row.invoice_id)
      if (!invoiceId) {
        return null
      }

      const invoice = invoiceMap.get(invoiceId)
      if (!invoice) {
        return null
      }

      const reimbursement: ReimbursementRow = {
        id: toStringValue(row.id) || invoiceId,
        invoice_id: invoiceId,
        provider_id: toStringValue(row.provider_id),
        patient_id: patientId,
        amount: toNumber(row.amount, 0),
        reimbursement_date:
          toStringValue(row.reimbursement_date) ||
          toStringValue(row.payment_date) ||
          new Date().toISOString().split('T')[0],
        status: mapStoredStatusToUi(String(row.status || 'pending')) as Reimbursement['status'],
        reference_number: toStringValue(row.reference_number),
        notes: toStringValue(row.notes),
        processing_notes: toStringValue(row.notes),
        created_at: toStringValue(row.created_at) || new Date().toISOString()
      }

      return {
        ...enrichInvoice(invoice),
        reimbursement
      }
    })
    .filter((item): item is MedicalInvoice & { reimbursement: ReimbursementRow } => Boolean(item))
}

export async function createReimbursementClaim(invoiceId: string, expectedAmount: number): Promise<Reimbursement> {
  if (!await isReimbursementFeatureAvailable()) {
    throw new Error('Le suivi des remboursements n’est pas encore activé sur ce projet Supabase.')
  }

  const { data: invoice, error: invoiceError } = await supabase
    .from('medical_invoices')
    .select('id, patient_id')
    .eq('id', invoiceId)
    .single()

  if (invoiceError) {
    throw new Error(`Erreur lecture facture: ${invoiceError.message}`)
  }

  const payload = {
    invoice_id: invoiceId,
    patient_id: invoice.patient_id,
    amount: expectedAmount,
    reimbursement_date: new Date().toISOString().split('T')[0],
    status: 'pending',
    notes: 'Demande de remboursement créée automatiquement'
  }

  const { data, error } = await supabase
    .from('reimbursements')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur création demande remboursement: ${error.message}`)
  }

  return data as Reimbursement
}

export async function updateReimbursementStatus(
  invoiceId: string,
  status: ReimbursementStatus['status'],
  actualAmount?: number,
  notes?: string
): Promise<void> {
  if (!await isReimbursementFeatureAvailable()) {
    return
  }

  const updateData: Record<string, unknown> = {
    status: mapUiStatusToStored(status),
    notes: notes ?? null
  }

  if (actualAmount !== undefined) {
    updateData.amount = actualAmount
  }

  if (status === 'paid') {
    updateData.reimbursement_date = new Date().toISOString().split('T')[0]
  }

  const { error } = await supabase
    .from('reimbursements')
    .update(updateData)
    .eq('invoice_id', invoiceId)

  if (error) {
    throw new Error(`Erreur mise à jour remboursement: ${error.message}`)
  }
}

export async function getReimbursementsByInvoice(invoiceId: string): Promise<Reimbursement[]> {
  if (!await isReimbursementFeatureAvailable()) {
    return []
  }

  const { data, error } = await supabase
    .from('reimbursements')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('created_at', { ascending: false })

  if (error) {
    if (isMissingRelationError(error, ['public.reimbursements', 'reimbursements'])) {
      return []
    }
    throw new Error(`Erreur récupération remboursements: ${error.message}`)
  }

  return (data || []) as Reimbursement[]
}

export async function getPendingReimbursements(patientId: string): Promise<Array<MedicalInvoice & { reimbursement: Reimbursement }>> {
  return listTrackedReimbursements(patientId, ['pending', 'partial'])
}

export function calculateExpectedReimbursement(
  totalAmount: number,
  insuranceProvider: string | null | undefined,
  consultationType?: string | null
): number {
  const normalizedProvider = normalizeText(insuranceProvider)
  const normalizedConsultation = normalizeText(consultationType)

  let secuBase = 25
  if (normalizedConsultation.includes('special')) secuBase = 30
  if (normalizedConsultation.includes('cardio')) secuBase = 42
  if (normalizedConsultation.includes('laboratoire') || normalizedConsultation.includes('analyse')) secuBase = totalAmount
  if (normalizedConsultation.includes('radio') || normalizedConsultation.includes('imagerie')) secuBase = totalAmount * 0.7

  const secuReimbursement = Math.min(totalAmount, secuBase) * 0.7

  let mutuelleRate = 0.75
  if (normalizedProvider.includes('mgen') || normalizedProvider.includes('mutuelle_generale')) mutuelleRate = 0.9
  if (normalizedProvider.includes('harmonie') || normalizedProvider.includes('maif')) mutuelleRate = 0.85
  if (normalizedProvider.includes('axa') || normalizedProvider.includes('allianz') || normalizedProvider.includes('april')) mutuelleRate = 0.8

  const remaining = Math.max(totalAmount - secuReimbursement, 0)
  return Math.round((secuReimbursement + (remaining * mutuelleRate)) * 100) / 100
}

export function getEstimatedReimbursementDays(insuranceProvider: string | null | undefined): number {
  const normalizedProvider = normalizeText(insuranceProvider)

  if (normalizedProvider.includes('cpam')) return 14
  if (normalizedProvider.includes('mgen') || normalizedProvider.includes('mutuelle_generale')) return 7
  if (normalizedProvider.includes('harmonie') || normalizedProvider.includes('maif')) return 10
  if (normalizedProvider.includes('macif') || normalizedProvider.includes('matmut')) return 12

  return 15
}

export async function checkOverdueReimbursements(patientId: string): Promise<Array<{
  invoice: MedicalInvoice
  reimbursement: Reimbursement
  daysOverdue: number
}>> {
  const trackedItems = await listTrackedReimbursements(patientId, ['pending', 'partial'])
  const now = new Date()

  return trackedItems
    .map((item) => {
      const createdAt = new Date(item.reimbursement.created_at)
      const daysPassed = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
      const expectedDays = getEstimatedReimbursementDays((item as any).insurance_provider)

      if (daysPassed <= expectedDays) {
        return null
      }

      return {
        invoice: item,
        reimbursement: item.reimbursement,
        daysOverdue: daysPassed - expectedDays
      }
    })
    .filter((item): item is {
      invoice: MedicalInvoice
      reimbursement: Reimbursement
      daysOverdue: number
    } => Boolean(item))
}
